import type { ToolCallMessagePartProps } from '@assistant-ui/react'
import type { ToolArtifact } from '@web/adapter/toThreadMessages'
import { useOptionalBridge } from '@web/adapter/useBridgeRuntime'
import type { AppToolPresentation } from '@web/api/types'
import {
  isDataObject,
  readableResult,
  TechnicalDetails,
} from '@web/components/tools/StructuredContent'
import { ToolShell, toolState } from '@web/components/tools/ToolShell'
import { Icon } from '@web/components/ui/icons'
import '@web/components/tools/analysis-artifact.css'

function plain(value: unknown, limit = 180): string {
  if (!['string', 'number', 'boolean'].includes(typeof value)) return '—'
  return String(value).slice(0, limit)
}

/** Present declared analysis data as text and bounded tables with session-scoped inspection. */
export function AnalysisArtifactTool(
  props: ToolCallMessagePartProps & { presentation: AppToolPresentation },
) {
  const bridge = useOptionalBridge()
  const tool = props.artifact as ToolArtifact | undefined
  const status = toolState(
    tool,
    props.result,
    props.isError,
    bridge?.snapshot.acp.running ?? true,
  )
  const result = readableResult(props.result)
  const data =
    isDataObject(result) && isDataObject(result.data) ? result.data : null
  const artifact =
    data && isDataObject(data.artifact) && data.artifact.schema_version === 1
      ? data.artifact
      : null
  const node = data && isDataObject(data.node) ? data.node : null
  const input = isDataObject(props.args.input) ? props.args.input : props.args
  const nodeId =
    typeof node?.id === 'string'
      ? node.id
      : typeof input.node_id === 'string'
        ? input.node_id
        : ''
  const config = bridge?.snapshot.bridge.record?.config
  const repositories = Array.isArray(config?.startup_values?.repositories)
    ? config.startup_values.repositories.filter(isDataObject)
    : []
  const repository = repositories.find(
    (item) =>
      typeof item.id === 'string' &&
      (item.id === node?.repo_id || nodeId.startsWith(`${item.id}:`)),
  )
  const heading =
    status === 'done'
      ? (props.presentation.completed_label ?? props.presentation.title)
      : ['pending', 'running'].includes(status)
        ? (props.presentation.running_label ?? props.presentation.title)
        : props.presentation.title
  const bindingId = config?.app_binding_id
  const artifactId =
    typeof artifact?.id === 'string' &&
    /^artifact-[a-f0-9]{20}$/.test(artifact.id)
      ? artifact.id
      : null
  const canInspect =
    config?.app_id === 'chronicle' &&
    !!bindingId &&
    !!artifactId &&
    /^[A-Za-z0-9_:-]{1,180}$/.test(nodeId)
  return (
    <ToolShell
      icon="activity"
      name={heading}
      artifact={tool}
      result={props.result}
      isError={props.isError}
    >
      <div className="analysis-tool">
        <div className="analysis-tool-kicker">
          <Icon name="repo" size={12} />
          {repository
            ? plain(repository.full_name ?? repository.name)
            : 'Portfolio analysis'}
        </div>
        {status === 'done' && artifact ? (
          <>
            <h3>{plain(artifact.title)}</h3>
            <p>{plain(artifact.summary, 1000)}</p>
            <div className="analysis-tool-footer">
              <span>
                <Icon name="fileText" size={12} />
                {typeof artifact.row_count === 'number'
                  ? `${artifact.row_count.toLocaleString()} saved rows`
                  : 'Evidence saved'}
              </span>
              {canInspect && (
                <button
                  className="btn btn-sm"
                  onClick={() =>
                    window.dispatchEvent(
                      new CustomEvent('bridge:analysis-inspect', {
                        detail: { bindingId, nodeId, artifactId },
                      }),
                    )
                  }
                >
                  Open analysis
                  <Icon name="chevronRight" size={12} />
                </button>
              )}
            </div>
          </>
        ) : status === 'error' ? (
          <p className="text-danger">
            This analysis needs attention. Your completed evidence remains
            saved.
          </p>
        ) : status === 'cancelled' ? (
          <p>Analysis paused. Continue from the workspace when you’re ready.</p>
        ) : (
          <p>Reading the repository evidence and preparing a saved analysis.</p>
        )}
      </div>
      <TechnicalDetails args={props.args} result={props.result} />
    </ToolShell>
  )
}
